import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { ShoppingCart, User, Menu, X, LogOut, LayoutDashboard, Calendar, Heart, Phone, Award, Package, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { getItemCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'doctor') return '/doctor';
    return '/dashboard';
  };

  const isStaff = user?.role === 'admin' || user?.role === 'cajera';

  return (
    <nav className="sticky top-0 z-50 glass border-b border-blue-100 bg-gradient-to-r from-white to-blue-50/50">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_pharmacy-plus-10/artifacts/yb0gzfm9_Logo%20.png" 
              alt="J3A Farmacias"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/productos" 
              className="text-slate-600 hover:text-[#2563EB] font-medium transition-colors"
              data-testid="nav-productos"
            >
              Productos
            </Link>
            <Link 
              to="/citas" 
              className="text-slate-600 hover:text-[#2563EB] font-medium transition-colors"
              data-testid="nav-citas"
            >
              Citas Médicas
            </Link>
            <a 
              href="tel:6143844739" 
              className="flex items-center gap-1 text-[#2563EB] font-medium transition-colors"
            >
              <Phone className="w-4 h-4" />
              614 384 4739
            </a>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Points Badge - visible for authenticated users */}
            {isAuthenticated && user?.points !== undefined && (
              <Link 
                to="/dashboard?tab=points" 
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-400 to-amber-500 text-white rounded-full font-bold text-sm hover:from-amber-500 hover:to-amber-600 transition-all shadow-sm"
                data-testid="nav-points-badge"
              >
                <Award className="w-4 h-4" />
                <span>{user.points || 0}</span>
              </Link>
            )}
            
            {/* Cart */}
            <Link to="/carrito" className="relative p-2" data-testid="nav-cart">
              <ShoppingCart className="w-6 h-6 text-slate-600 hover:text-[#E52225] transition-colors" />
              {getItemCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E52225] text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {getItemCount()}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="nav-user-menu">
                    <User className="w-6 h-6 text-slate-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    {user?.points > 0 && (
                      <p className="text-xs text-[#E52225] font-semibold mt-1">
                        {user.points} puntos
                      </p>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="nav-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Mi Panel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/citas')} data-testid="nav-mis-citas">
                    <Calendar className="w-4 h-4 mr-2" />
                    Mis Citas
                  </DropdownMenuItem>
                  {isStaff && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/inventario')} data-testid="nav-inventario">
                        <Package className="w-4 h-4 mr-2" />
                        Inventario
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/contabilidad')} data-testid="nav-contabilidad">
                        <Calculator className="w-4 h-4 mr-2" />
                        Contabilidad
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="nav-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="hidden sm:flex"
                  data-testid="nav-login"
                >
                  Iniciar Sesión
                </Button>
                <Button 
                  onClick={() => navigate('/registro')}
                  className="btn-primary h-10 px-4"
                  data-testid="nav-register"
                >
                  Registrarse
                </Button>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 animate-fadeIn">
            <div className="flex flex-col gap-2">
              <Link 
                to="/productos" 
                className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Productos
              </Link>
              <Link 
                to="/citas" 
                className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Citas Médicas
              </Link>
              {isStaff && (
                <>
                  <Link 
                    to="/inventario" 
                    className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Package className="w-4 h-4" />
                    Inventario
                  </Link>
                  <Link 
                    to="/contabilidad" 
                    className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Calculator className="w-4 h-4" />
                    Contabilidad
                  </Link>
                </>
              )}
              <a 
                href="tel:6143844739" 
                className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                614 384 4739
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
